import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import underwriting from '../services/UnderwritingService';
import application from '../services/ApplicationService';
import {
  ure_slice_name,
  ure_thunks,
  IS_CLOSABLE_MENU_ITEM_NAME,
} from '../constants/underwriting';
import {
  getApplicantIndexFromPayloadData,
  getApplicantIndexFromMeta,
  getUWAnswersByApplicantId,
  getApplicantIndexFromApplicantId,
  getAnswersFromMeta,
  getApplicantAnswersFromApplicationId,
} from './UnderwritingSelectors';
import {
  postStartApplication,
  fetchApplicationById,
  postAddApplicant,
  deleteApplicantById,
} from './ApplicationSlice';

const uwState = {
  section: {
    selected: null,
    loading: false,
    error: false,
  },
  enquiry: {
    id: '',
    enquiryId: '',
    applicantId: null,
    branch: '',
    tag: '',
    locale: '',
    sections: [],
    allAnswers: {},
    availableConditions: [],
    exclusives: [],
    loading: false,
    error: false,
    preCheck: false,
    isCloseable: false,
    isOpen: false,
    requestId: null,
  },
  declaration: {
    acceptedPreUnderwriting: false,
    acceptedPostUnderwriting: false,
    loading: false,
    error: false,
  },
};

export const initialData = {
  applicants: [{ ...uwState }, { ...uwState }],
};

export const fetchStartEnquiry = createAsyncThunk(
  ure_thunks.URE_FETCH_START_ENQUIRY,
  async (applicantId) => {
    const response =
      await underwriting.fetchApplicantUnderwritingEnquiryByApplicantId(
        applicantId
      );
    return response.data;
  }
);

export const fetchEnquiryById = createAsyncThunk(
  ure_thunks.URE_FETCH_ENQUIRY_BY_ID,
  async (applicantId) => {
    const response = await underwriting.getEnquiryById(applicantId);
    return response.data;
  }
);

export const postEquiryById = createAsyncThunk(
  ure_thunks.URE_POST_ENQUIRY_BY_ID,
  async ({ applicantId, data, isQi }, { getState, rejectWithValue }) => {
    try {
      const answers = getApplicantAnswersFromApplicationId(
        getState(),
        applicantId
      );
      const response = await underwriting.postEnquiryById(
        applicantId,
        { ...answers, ...data },
        isQi
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchLookUpQuestionOption = createAsyncThunk(
  ure_thunks.URE_FETCH_LOOKING_QUESTION_OPTION,
  async (data) => {
    const response = await underwriting.getLookUpQuestionOption(data);
    return response.data;
  }
);

export const postCloseEnquiry = createAsyncThunk(
  ure_thunks.URE_POST_CLOSE_ENQUIRY,
  async (applicantId, { rejectWithValue }) => {
    try {
      const response = await underwriting.postCloseEnquiry(applicantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postCreateApplicantUnderwritingEnquiryByApplicantId =
  createAsyncThunk(
    ure_thunks.URE_POST_CREATE_APPLICATION_UNDERWRITING_ENQUIRY,
    async (applicationId) => {
      const response =
        await application.postCreateApplicantUnderwritingEnquiryByApplicantId(
          applicationId
        );
      return response.data;
    }
  );

export const fetchApplicantUnderwritingEnquiryByApplicantId = createAsyncThunk(
  ure_thunks.URE_FETCH_APPLICANT_UNDERWRITING_ENQUIRY,
  async (applicationId) => {
    const response =
      await application.fetchApplicantUnderwritingEnquiryByApplicantId(
        applicationId
      );

    return response.data;
  }
);

export const postApplicationEnquiryById = createAsyncThunk(
  ure_thunks.URE_POST_APPLICATION_ENQUIRY_BY_ID,
  async ({ applicantId, data, isQi }, store) => {
    const response = await application.postApplicationEnquiryById(
      applicantId,
      data,
      getUWAnswersByApplicantId(store.getState(), applicantId),
      isQi
    );
    return response.data;
  }
);

export const postAcceptPreUnderwritingDeclarationById = createAsyncThunk(
  ure_thunks.URE_POST_ACCEPT_PRE_UNDERWRITING_DECLARATION_BY_ID,
  async (applicantId, store) => {
    const state = store.getState();
    const response =
      await underwriting.postAcceptPreUnderwritingDeclarationById(
        state.application.id,
        applicantId
      );
    return response.data;
  }
);

export const postAcceptPostUnderwritingDeclarationById = createAsyncThunk(
  ure_thunks.URE_POST_ACCEPT_POST_UNDERWRITING_DECLARATION_BY_ID,
  async ({ applicantId }, store) => {
    const state = store.getState();
    const response =
      await underwriting.postAcceptPostUnderwritingDeclarationById(
        state.application.id,
        applicantId
      );
    return response.data;
  }
);

export const postUnderwritingStartPreSale = createAsyncThunk(
  ure_thunks.URE_POST_UNDERWRITING_START_PRESALE,
  async ({ applicantId, agentId }) => {
    const response = await underwriting.postUnderwritingStartPreSale(
      applicantId,
      agentId
    );
    return response.data;
  }
);

export const underwritingSlice = createSlice({
  name: ure_slice_name,
  initialState: initialData,
  reducers: {
    reset() {
      return initialData;
    },
    addSection(state, action) {
      const index = getApplicantIndexFromApplicantId(
        state,
        action.payload.applicantId
      );
      state.applicants[index].section.selected = action.payload.item;
    },
    deleteAvailableConditions(state, action) {
      const index = getApplicantIndexFromApplicantId(state, action.payload);
      state.applicants[index].enquiry.availableConditions = [];
    },
    setExclusive(state, action) {
      const index = getApplicantIndexFromApplicantId(
        state,
        action.payload.applicantId
      );
      const exclusives = [
        ...state.applicants[index].enquiry.exclusives,
        action.payload.data,
      ];
      state.applicants[index].enquiry.exclusives = exclusives;
    },
    deleteExclusive(state, action) {
      const index = getApplicantIndexFromApplicantId(
        state,
        action.payload.applicantId
      );
      const exclusives = current(
        state.applicants[index].enquiry.exclusives
      ).filter((item) => {
        try {
          return Object.keys(item)[0] !== Object.keys(action.payload.data)[0];
        } catch {
          return false;
        }
      });
      state.applicants[index].enquiry.exclusives = exclusives;
    },
    setApplicant1Id(state, action) {
      state.applicants[0].enquiry.applicantId = action.payload;
    },
    setApplicant2Id(state, action) {
      state.applicants[1].enquiry.applicantId = action.payload;
    },
    acceptedPostUnderwritingDeclaration(state, action) {
      const index = getApplicantIndexFromApplicantId(
        state,
        action.payload.applicantId
      );
      state.applicants[index].declaration.acceptedPostUnderwriting =
        action.payload.accepted;
    },
    removeApplicant(state, action) {
      const index = getApplicantIndexFromApplicantId(
        state,
        action.payload.applicantId
      );
      if (index === 0) {
        state.applicants[0] = state.applicants[1];
        state.applicants[1] = uwState;
      } else if (index === 1) {
        state.applicants[1] = uwState;
      }
    },
  },
  extraReducers: {
    [fetchStartEnquiry.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      if (index !== -1) {
        state.applicants[index].section.loading = true;
        if (
          state.applicants[index].enquiry?.sections?.length > 0 &&
          (state.applicants[index].enquiry.isOpen === false ||
            state.applicants[index].enquiry.isCloseable === true)
        ) {
          state.applicants[index].section.selected = IS_CLOSABLE_MENU_ITEM_NAME;
        }
      }
    },
    [fetchStartEnquiry.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { extendedOnDate } = action.meta.arg;
      const { enquiryId, branch, tag, locale, sections, allAnswers, isOpen } =
        action.payload.data;
      state.applicants[index].enquiry.id = enquiryId;
      state.applicants[index].enquiry.branch = branch;
      state.applicants[index].enquiry.tag = tag;
      state.applicants[index].enquiry.locale = locale;
      state.applicants[index].enquiry.isOpen = isOpen;
      state.applicants[index].enquiry.sections = sections;
      if (
        state.applicants[0].section.selected === null &&
        state.applicants[1].section.selected === null
      ) {
        state.applicants[index].section.selected = sections[0].name;
      }
      if (
        state.applicants[0].section.selected !== null &&
        state.applicants[1].section.selected === null
      ) {
        state.applicants[index].section.selected = sections[0].name;
      }
      if (
        state.applicants[index].enquiry.isOpen === false ||
        state.applicants[index].enquiry.isCloseable === true
      ) {
        state.applicants[index].section.selected = IS_CLOSABLE_MENU_ITEM_NAME;
      }
      if (extendedOnDate) {
        state.applicants[index].section.selected = sections[0].name;
      }
      state.applicants[index].section.loading = false;
      state.applicants[index].enquiry.allAnswers = allAnswers;
    },
    [fetchStartEnquiry.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.error = action.error.message;
      state.applicants[index].section.loading = false;
    },
    [fetchEnquiryById.pending]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].section.loading = true;
    },
    [fetchEnquiryById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].enquiry.sections = action.payload.data?.sections;
      state.applicants[index].enquiry.allAnswers =
        action.payload.data?.allAnswers;
      state.applicants[index].enquiry.isOpen = action.payload.data?.isOpen;
      state.applicants[index].enquiry.isCloseable =
        action.payload.data?.isCloseable;
      state.applicants[index].enquiry.branch = action.payload.data?.branch;
      state.applicants[index].enquiry.tag = action.payload.data?.tag;
      state.applicants[index].enquiry.locale = action.payload.data?.locale;
      state.applicants[index].section.loading = false;
    },
    [fetchEnquiryById.rejected]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].section.loading = false;
    },
    [postEquiryById.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { data } = action.meta.arg;
      state.applicants[index].enquiry.allAnswers = {
        ...current(state).applicants[index].enquiry.allAnswers,
        ...data,
      };
      state.applicants[index].enquiry.loading = true;
      state.applicants[index].enquiry.questionResolving = data;
    },
    [postEquiryById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { data } = action.meta.arg;
      if (data === current(state).applicants[index].enquiry.questionResolving) {
        state.applicants[index].enquiry.sections = action.payload.data.sections;
        state.applicants[index].enquiry.allAnswers =
          action.payload.data.allAnswers;
        state.applicants[index].enquiry.isCloseable =
          action.payload.data.isCloseable;
        state.applicants[index].enquiry.isOpen = action.payload.data.isOpen;
        state.applicants[index].enquiry.loading = false;
      }
    },
    [postEquiryById.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { data } = action.meta.arg;
      if (data === current(state).applicants[index].enquiry.questionResolving) {
        state.applicants[index].enquiry.questionResolving = null;
      }
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].enquiry.loading = false;
    },
    [fetchLookUpQuestionOption.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].section.loading = true;
    },
    [fetchLookUpQuestionOption.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.availableConditions = [];
      state.applicants[index].enquiry.availableConditions = action.payload.data;
      state.applicants[index].section.loading = false;
    },
    [fetchLookUpQuestionOption.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].enquiry.loading = false;
    },
    [postCloseEnquiry.pending]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].enquiry.loading = true;
    },
    [postCloseEnquiry.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].enquiry.sections = action.payload.data.sections;
      state.applicants[index].enquiry.allAnswers =
        action.payload.data.allAnswers;
      state.applicants[index].enquiry.loading = false;
      state.applicants[index].enquiry.isOpen = action.payload.data.isOpen;
      state.applicants[index].enquiry.isCloseable =
        action.payload.data.isCloseable;
    },
    [postCloseEnquiry.rejected]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].enquiry.loading = false;
    },
    [postStartApplication.pending]: (state) => {
      state.applicants[0].enquiry.loading = true;
    },
    [postStartApplication.fulfilled]: (state, action) => {
      const { applicantId, allAnswers } = action.payload.data.enquiry;
      action.payload.data.application.applicants.forEach((applicant, index) => {
        state.applicants[index].enquiry.applicantId = applicantId;
        state.applicants[index].enquiry.loading = false;
        state.applicants[index].enquiry.allAnswers = allAnswers;
      });
    },
    [postStartApplication.rejected]: (state, action) => {
      state.applicants[0].enquiry.error = action.error.message;
      state.applicants[0].enquiry.loading = false;
    },
    [fetchApplicationById.pending]: (state) => {
      state.applicants[0].enquiry.loading = true;
    },
    [fetchApplicationById.fulfilled]: (state, action) => {
      const { applicants } = action.payload.data;
      applicants.forEach((applicant, index) => {
        state.applicants[index].enquiry.applicantId = applicant.applicantId;
        state.applicants[index].enquiry.loading = false;
      });
    },
    [fetchApplicationById.rejected]: (state, action) => {
      state.applicants[0].enquiry.error = action.error.message;
      state.applicants[0].enquiry.loading = false;
    },

    [postAddApplicant.pending]: (state) => {
      state.applicants[1].enquiry.loading = true;
    },
    [postAddApplicant.fulfilled]: (state, action) => {
      const { applicant } = action.payload.data;
      state.applicants[1].enquiry.applicantId = applicant.applicantId;
      state.applicants[1].enquiry.loading = false;
    },
    [postAddApplicant.rejected]: (state, action) => {
      state.applicants[1].enquiry.error = action.error.message;
      state.applicants[1].enquiry.loading = false;
    },
    [postCreateApplicantUnderwritingEnquiryByApplicantId.pending]: (
      state,
      action
    ) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.loading = true;
    },
    [postCreateApplicantUnderwritingEnquiryByApplicantId.fulfilled]: (
      state,
      action
    ) => {
      const { branch, tag, locale, sections, allAnswers } = action.payload.data;
      const index = getApplicantIndexFromPayloadData(state, action);
      state.applicants[index].enquiry.branch = branch;
      state.applicants[index].enquiry.tag = tag;
      state.applicants[index].enquiry.locale = locale;
      state.applicants[index].enquiry.sections = sections;
      state.applicants[index].enquiry.loading = false;
      state.applicants[index].enquiry.allAnswers = allAnswers;
    },
    [postCreateApplicantUnderwritingEnquiryByApplicantId.rejected]: (
      state,
      action
    ) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.error = action.error.message;
      state.applicants[index].enquiry.loading = false;
    },
    [fetchApplicantUnderwritingEnquiryByApplicantId.pending]: (
      state,
      action
    ) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.loading = true;
    },
    [fetchApplicantUnderwritingEnquiryByApplicantId.fulfilled]: (
      state,
      action
    ) => {
      const { branch, tag, locale, sections, allAnswers, isOpen, isCloseable } =
        action.payload.data;
      const index = getApplicantIndexFromPayloadData(state, action);
      state.applicants[index].enquiry.branch = branch;
      state.applicants[index].enquiry.tag = tag;
      state.applicants[index].enquiry.locale = locale;
      state.applicants[index].enquiry.sections = sections;
      state.applicants[index].enquiry.loading = false;
      state.applicants[index].enquiry.allAnswers = allAnswers;
      state.applicants[index].enquiry.preCheck = true;
      state.applicants[index].enquiry.isOpen = isOpen;
      state.applicants[index].enquiry.isCloseable = isCloseable;
    },
    [fetchApplicantUnderwritingEnquiryByApplicantId.rejected]: (
      state,
      action
    ) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.error = action.error.message;
      state.applicants[index].enquiry.loading = false;
      state.applicants[index].enquiry.preCheck = true;
    },
    [postApplicationEnquiryById.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].enquiry.loading = true;
      const answers = getAnswersFromMeta(
        action,
        current(state).applicants[index].enquiry.allAnswers
      );
      state.applicants[index].enquiry.allAnswers = answers;
    },
    [postApplicationEnquiryById.fulfilled]: (state, action) => {
      const { sections, allAnswers, branch, tag, locale, isOpen, isCloseable } =
        action.payload.data;
      const index = getApplicantIndexFromPayloadData(state, action);
      state.applicants[index].enquiry.branch = branch;
      state.applicants[index].enquiry.tag = tag;
      state.applicants[index].enquiry.locale = locale;
      state.applicants[index].enquiry.sections = sections;
      state.applicants[index].enquiry.allAnswers = allAnswers;
      state.applicants[index].enquiry.loading = false;
      state.applicants[index].enquiry.isOpen = isOpen;
      state.applicants[index].enquiry.isCloseable = isCloseable;
    },
    [postApplicationEnquiryById.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].enquiry.loading = false;
    },
    [postAcceptPreUnderwritingDeclarationById.pending]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].declaration.loading = true;
    },
    [postAcceptPreUnderwritingDeclarationById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].declaration.acceptedPreUnderwriting = true;
      state.applicants[index].declaration.loading = false;
    },
    [postAcceptPreUnderwritingDeclarationById.rejected]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].declaration.loading = false;
    },
    [postAcceptPostUnderwritingDeclarationById.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].declaration.loading = true;
    },
    [postAcceptPostUnderwritingDeclarationById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].declaration.acceptedPostUnderwriting = true;
      state.applicants[index].declaration.loading = false;
    },
    [postAcceptPostUnderwritingDeclarationById.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].section.error = action.error.message;
      state.applicants[index].declaration.loading = false;
    },
    [postUnderwritingStartPreSale.pending]: (state, action) => {
      const answers = getAnswersFromMeta(
        action,
        current(state).applicants[0].enquiry.allAnswers
      );
      state.applicants[0].enquiry.allAnswers = answers;
      state.applicants[0].enquiry.loading = true;
    },
    [postUnderwritingStartPreSale.fulfilled]: (state, action) => {
      const {
        arg: { applicantId },
      } = action.meta;
      state.applicants[0].enquiry.applicantId = applicantId;
      state.applicants[0].enquiry.sections = action.payload.data.sections;
      state.applicants[0].enquiry.allAnswers = action.payload.data.allAnswers;
      state.applicants[0].enquiry.isCloseable = action.payload.data.isCloseable;
      state.applicants[0].enquiry.isOpen = action.payload.data.isOpen;
      state.applicants[0].enquiry.loading = false;
    },
    [postUnderwritingStartPreSale.rejected]: (state, action) => {
      state.applicants[0].section.error = action.error.message;
      state.applicants[0].enquiry.loading = false;
    },
    [deleteApplicantById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromApplicantId(state, action.meta.arg);
      if (index === 0) {
        state.applicants[0] = state.applicants[1];
        state.applicants[1] = uwState;
      } else if (index === 1) {
        state.applicants[1] = uwState;
      }
    },
  },
});

export const {
  reset,
  addSection,
  deleteAvailableConditions,
  setExclusive,
  deleteExclusive,
  setApplicant1Id,
  setApplicant2Id,
  acceptedPostUnderwritingDeclaration,
  removeApplicant,
} = underwritingSlice.actions;

export default underwritingSlice;
